import React, { useState } from 'react';
import { Table, Button, IconButton, SelectPicker, Form, Panel, Message } from 'rsuite';
import TrashIcon from '@rsuite/icons/Trash';
import { AxiosError } from 'axios';
import PageContent from '@/components/PageContent';
import ErrorMessage from '@/components/ErrorMessage';
import FetchingSelect from '@/components/FetchingSelect';
import { BaseErrorModel } from '@/models/core.model';
import { PointAccount, PointAccountAssignment, PointAccountTargetType, PointAccountTargetTypeList } from '@/models/point-account.model';
import { addPointAccountAssignment, removePointAccountAssignment } from '@/services/point-account.service';
import { getRunningClubsLookup } from '@/services/running-club.service';
import { getRunningClubEventsLookup } from '@/services/running-club-event.service';
import { getEnergyHubsLookup } from '@/services/energy-hub.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import AssignmentTargetName from './assignment-target-name';

const { Column, HeaderCell, Cell } = Table;

interface Props {
  account: PointAccount;
  onChanged: () => void;
}

const PointAccountAssignmentList: React.FC<Props> = ({ account, onChanged }) => {
  const [targetType, setTargetType] = useState<PointAccountTargetType>(PointAccountTargetType.RunningClub);
  const [clubId, setClubId] = useState<number | undefined>(undefined);
  const [eventId, setEventId] = useState<number | undefined>(undefined);
  const [hubId, setHubId] = useState<number | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);

  const isPlatform = targetType == PointAccountTargetType.Platform;
  const isHub = targetType == PointAccountTargetType.EnergyHub;
  // Platform has no target entity; the API stores its targetId as 0.
  const targetId = isPlatform ? 0
    : isHub ? hubId
    : targetType == PointAccountTargetType.RunningClub ? clubId : eventId;

  const handleAdd = async () => {
    if (targetId === undefined) return;
    setErrorModel(undefined);
    setSaving(true);
    try {
      const result = await addPointAccountAssignment(account.id, { targetType, targetId });
      if (result.success) {
        toastSuccessMessage('Assignment added');
        setEventId(undefined);
        onChanged();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (row: PointAccountAssignment) => {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      await removePointAccountAssignment(account.id, row.id);
      toastSuccessMessage('Assignment removed');
      onChanged();
    } catch (err) {
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in removing assignment: ${em?.errorMessage}`);
    }
  };

  return (
    <PageContent>
      <Message type="info" showIcon style={{ marginBottom: 16 }}>
        A Running Club assignment covers every event of that club; a Running Club Event assignment covers only that event.
        A Platform assignment is app-wide (used by User Sign-Up rules, and by Energy Hub rules for every hub).
        An Energy Hub assignment covers only that venue.
      </Message>
      <Panel bordered header="Add assignment" style={{ marginBottom: 16 }}>
        <Form layout="inline">
          <Form.Group>
            <SelectPicker data={PointAccountTargetTypeList} searchable={false} cleanable={false} value={targetType}
              onChange={val => { setTargetType(val ?? PointAccountTargetType.RunningClub); setEventId(undefined); }} style={{ width: 200 }} />
          </Form.Group>
          {isHub && <Form.Group>
            <FetchingSelect key="hub" fetchData={(text, page) => getEnergyHubsLookup(text, page)} value={hubId}
              onChange={val => setHubId(val ?? undefined)} placeholder="Select energy hub" style={{ width: 280 }} />
          </Form.Group>}
          {!isPlatform && !isHub && <Form.Group>
            <FetchingSelect key="club" fetchData={(text, page) => getRunningClubsLookup(text, page)} value={clubId}
              onChange={val => { setClubId(val ?? undefined); setEventId(undefined); }}
              placeholder={targetType == PointAccountTargetType.RunningClub ? 'Select club' : 'Filter events by club (optional)'}
              style={{ width: 280 }} />
          </Form.Group>}
          {targetType == PointAccountTargetType.RunningClubEvent && (
            <Form.Group>
              <FetchingSelect key={`events-${clubId ?? 'all'}`} reFetchOnOpen
                fetchData={(text, page) => getRunningClubEventsLookup(text, clubId, page)} value={eventId}
                onChange={val => setEventId(val ?? undefined)} placeholder="Select event" style={{ width: 280 }} />
            </Form.Group>
          )}
          <Button appearance="primary" onClick={handleAdd} loading={saving} disabled={targetId === undefined}>Assign</Button>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 12 }} />}
      </Panel>

      <Table autoHeight data={account.assignments}>
        <Column flexGrow={1}>
          <HeaderCell>Target Type</HeaderCell>
          <Cell>{(row: PointAccountAssignment) => PointAccountTargetTypeList.find(t => t.value == row.targetType)?.label ?? row.targetType}</Cell>
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>Target</HeaderCell>
          <Cell>{(row: PointAccountAssignment) => <AssignmentTargetName targetType={row.targetType} targetId={row.targetId} />}</Cell>
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(row: PointAccountAssignment) => (
              <IconButton icon={<TrashIcon />} circle size="sm" color="red" appearance="ghost" onClick={() => handleRemove(row)} />
            )}
          </Cell>
        </Column>
      </Table>
    </PageContent>
  );
};

export default PointAccountAssignmentList;
